/**
 * Bell (interaction) vs sidebar (operations) channel classification.
 * Single source for count/tray wiring — maps stay in notificationSidebarAllocation.ts.
 *
 * Role-aware: the same type string can be AR for broker/shipper and ops for carrier
 * (e.g. make_offer is sent to broker as Action Required, not as a carrier sidebar dot).
 */

import {
  BROKER_SHIPPER_BELL_ONLY_NOTIFICATION_TYPES,
  BROKER_SHIPPER_SIDEBAR_NOTIFICATION_TYPES,
  CARRIER_SIDEBAR_NOTIFICATION_TYPES,
  CarrierSidebarPage,
  BrokerShipperSidebarPage,
  getBrokerShipperSidebarPageForType,
  getCarrierSidebarPageForType,
} from './notificationSidebarAllocation';
import {
  DASHBOARD_ACTION_REQUIRED_NOTIFICATION_TYPES,
  isDashboardActionRequiredNotificationType,
  NOTIFICATION_TYPES,
} from './notificationTypes';

export type NotificationRole = 'broker' | 'shipper' | 'carrier';

export type NotificationChannel = 'interaction' | 'operations' | 'action_required' | 'unclassified';

/** Carrier bell / tray — decision notices and informational (not sidebar ops). */
export const CARRIER_INTERACTION_NOTIFICATION_TYPES: readonly string[] = [
  NOTIFICATION_TYPES.OFFER_REJECTED,
  NOTIFICATION_TYPES.CARRIER_DECLINE,
  'carrier_reject',
  NOTIFICATION_TYPES.SYSTEM_ALERT,
  NOTIFICATION_TYPES.RATING_RECEIVED,
  NOTIFICATION_TYPES.MESSAGE_RECEIVED,
  NOTIFICATION_TYPES.CARRIER_OFFLINE,
  NOTIFICATION_TYPES.MARKETPLACE,
];

const ALL_BROKER_SHIPPER_OPS_TYPES = new Set(
  Object.values(BROKER_SHIPPER_SIDEBAR_NOTIFICATION_TYPES).flat()
);

const ALL_CARRIER_OPS_TYPES = new Set(
  Object.values(CARRIER_SIDEBAR_NOTIFICATION_TYPES).flat()
);

const BROKER_SHIPPER_INTERACTION_TYPES = new Set<string>([
  ...BROKER_SHIPPER_BELL_ONLY_NOTIFICATION_TYPES,
]);

export function getSidebarPageForNotificationType(
  type: string,
  role: NotificationRole
): BrokerShipperSidebarPage | CarrierSidebarPage | null {
  if (role === 'carrier') return getCarrierSidebarPageForType(type);
  return getBrokerShipperSidebarPageForType(type);
}

export function isBrokerShipperOperationsNotificationType(type: string): boolean {
  return ALL_BROKER_SHIPPER_OPS_TYPES.has(type);
}

export function isCarrierOperationsNotificationType(type: string): boolean {
  return ALL_CARRIER_OPS_TYPES.has(type);
}

export function isOperationsNotificationType(type: string, role: NotificationRole): boolean {
  if (role === 'carrier') return isCarrierOperationsNotificationType(type);
  return isBrokerShipperOperationsNotificationType(type);
}

export function isBrokerShipperInteractionNotificationType(type: string): boolean {
  if (isDashboardActionRequiredNotificationType(type)) return false;
  if (isBrokerShipperOperationsNotificationType(type)) return false;
  return BROKER_SHIPPER_INTERACTION_TYPES.has(type);
}

export function isCarrierInteractionNotificationType(type: string): boolean {
  if (isCarrierOperationsNotificationType(type)) return false;
  return (CARRIER_INTERACTION_NOTIFICATION_TYPES as readonly string[]).includes(type);
}

export function isInteractionNotificationType(type: string, role: NotificationRole): boolean {
  if (role === 'carrier') return isCarrierInteractionNotificationType(type);
  return isBrokerShipperInteractionNotificationType(type);
}

/**
 * Classify a notification type for the given recipient role.
 * Unclassified types appear on neither bell nor sidebar until Phase 3 emit/map coverage.
 */
export function getNotificationChannel(type: string, role: NotificationRole): NotificationChannel {
  if (role !== 'carrier' && isDashboardActionRequiredNotificationType(type)) {
    return 'action_required';
  }
  if (isOperationsNotificationType(type, role)) return 'operations';
  if (isInteractionNotificationType(type, role)) return 'interaction';
  return 'unclassified';
}

/** Exported for tests / debugging — must stay aligned with DASHBOARD_ACTION_REQUIRED_NOTIFICATION_TYPES. */
export const ACTION_REQUIRED_NOTIFICATION_TYPES = DASHBOARD_ACTION_REQUIRED_NOTIFICATION_TYPES;
